import { useCallback, useEffect, useRef, useState } from 'react';

import { apiGet, ApiError } from '../widget-api/http-client';

/** Ответ GET /api/v1/public/auth-config. */
export type AuthConfig = {
  captcha_enabled: boolean;
  captcha_sitekey: string;
  captcha_provider?: string;
  /** IP в allowlist обхода: капча включена, но для этого клиента не показывается. */
  captcha_bypass: boolean;
};

export type AuthConfigStatus = 'loading' | 'ready' | 'error';

export type AuthConfigState = {
  status: AuthConfigStatus;
  config: AuthConfig | null;
  error: string | null;
};

const CONFIG_ERROR_MESSAGE = 'Не удалось проверить защиту от роботов. Проверьте соединение и повторите.';

let cachedConfig: AuthConfig | null = null;
let inflightConfig: Promise<AuthConfig> | null = null;

/** Сброс кэша конфига: следующий fetchAuthConfig пойдёт в сеть. */
export function invalidateAuthConfig(): void {
  cachedConfig = null;
  inflightConfig = null;
}

function normalizeAuthConfig(cfg: Partial<AuthConfig> | null): AuthConfig {
  return {
    captcha_enabled: Boolean(cfg?.captcha_enabled),
    captcha_sitekey: cfg?.captcha_sitekey ?? '',
    captcha_provider: cfg?.captcha_provider,
    captcha_bypass: Boolean(cfg?.captcha_bypass)
  };
}

/**
 * Конфигурация авторизации (капча). Успешный ответ кэшируется на время жизни страницы;
 * сетевые и HTTP-ошибки НЕ кэшируются как «капча выключена» — иначе submit уходит без токена
 * и сервер отвечает 400 captcha_required.
 */
export function fetchAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (inflightConfig) return inflightConfig;
  const request = apiGet<Partial<AuthConfig> | null>('/api/v1/public/auth-config')
    .then((cfg) => {
      const normalized = normalizeAuthConfig(cfg);
      cachedConfig = normalized;
      return normalized;
    })
    .finally(() => {
      inflightConfig = null;
    });
  inflightConfig = request;
  return request;
}

export type AuthConfigHook = AuthConfigState & {
  /** Повторная загрузка конфига (кнопка «Повторить»). */
  refresh: () => void;
  /** Принудительная загрузка; используется retry-логикой после captcha_required. */
  reload: (force?: boolean) => Promise<AuthConfig>;
};

/** Хук: конфиг капчи с явными состояниями loading/ready/error. */
export function useAuthConfig(): AuthConfigHook {
  const [state, setState] = useState<AuthConfigState>({
    status: 'loading',
    config: null,
    error: null
  });
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reload = useCallback(async (force = false): Promise<AuthConfig> => {
    if (force) invalidateAuthConfig();
    if (mountedRef.current) setState({ status: 'loading', config: null, error: null });
    try {
      const cfg = await fetchAuthConfig();
      if (mountedRef.current) setState({ status: 'ready', config: cfg, error: null });
      return cfg;
    } catch (err) {
      if (mountedRef.current) setState({ status: 'error', config: null, error: CONFIG_ERROR_MESSAGE });
      throw err;
    }
  }, []);

  useEffect(() => {
    void reload(false).catch(() => {
      /* состояние error уже выставлено; UI показывает повтор */
    });
  }, [reload]);

  const refresh = useCallback(() => {
    void reload(true).catch(() => {
      /* состояние error уже выставлено; UI показывает повтор */
    });
  }, [reload]);

  return { ...state, refresh, reload };
}

type SmartCaptchaApi = {
  render: (container: HTMLElement | string, params: Record<string, unknown>) => number;
  reset: (widgetId?: number) => void;
  execute: (widgetId?: number) => void;
  destroy: (widgetId?: number) => void;
  subscribe: (widgetId: number, event: string, callback: (arg?: unknown) => void) => () => void;
  getResponse: (widgetId?: number) => string;
};

declare global {
  interface Window {
    smartCaptcha?: SmartCaptchaApi;
    __domcrmSmartCaptchaOnload?: () => void;
  }
}

const SCRIPT_SRC =
  'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=__domcrmSmartCaptchaOnload';

/** Таймаут ожидания токена: если задание закрыли/не завершили — даём повторить. */
const CAPTCHA_TIMEOUT_MS = 60000;

let scriptPromise: Promise<void> | null = null;

/** Загружает captcha.js один раз на страницу (onload-колбэк → window.smartCaptcha). */
export function loadSmartCaptchaScript(): Promise<void> {
  if (window.smartCaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const prev = window.__domcrmSmartCaptchaOnload;
    window.__domcrmSmartCaptchaOnload = () => {
      prev?.();
      resolve();
    };
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Не удалось загрузить SmartCaptcha'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function isMobileWebView(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('widget_channel') === 'mobile';
}

/** Тестовый форс виджета на bypass-IP (только для mobile-gate/e2e): ?captcha_test=1. */
function isCaptchaForcedForTest(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('captcha_test') === '1';
}

/** Капча настроена на сервере (включена + sitekey), независимо от bypass-IP. */
export function isCaptchaServerEnabled(cfg: AuthConfig | null | undefined): boolean {
  return Boolean(cfg?.captcha_enabled && cfg?.captcha_sitekey);
}

/** Капча реально нужна этому клиенту (включена и IP не в bypass-списке). */
export function isCaptchaEnabled(cfg: AuthConfig | null | undefined): boolean {
  if (!cfg || !isCaptchaServerEnabled(cfg)) return false;
  return !cfg.captcha_bypass || isCaptchaForcedForTest();
}

/** 400 captcha_required от сервера: токен не получен или устарел. */
export function isCaptchaRequiredError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.errorType === 'captcha_required') return true;
  return err.status === 400 && /captcha[_\s-]?required/i.test(err.detail);
}

export type SmartCaptchaController = {
  /** Капча включена на бэкенде (SMARTCAPTCHA_MODE=required + sitekey). */
  enabled: boolean;
  /** Серверная капча включена (есть sitekey): юридическое уведомление показываем даже при bypass. */
  serverEnabled: boolean;
  /** IP в bypass-списке: проверка не выполняется, запросы уходят без токена. */
  bypass: boolean;
  /** loading — конфиг ещё грузится; ready — можно отправлять; error — конфиг не получен. */
  status: AuthConfigStatus;
  /** Текст ошибки загрузки конфига (null, если ошибки нет). */
  error: string | null;
  /** true, пока конфиг загружается: submit нужно блокировать. */
  loading: boolean;
  /** false только на время загрузки конфига. */
  canSubmit: boolean;
  /** Повторная загрузка конфига (кнопка «Повторить»). */
  refresh: () => void;
  /** Запускает невидимую проверку и резолвится одноразовым токеном. */
  execute: () => Promise<string>;
  /** Сброс состояния виджета (после неудачного сабмита). */
  reset: () => void;
  /** Яндекс показывает задание (подозрительный запрос). */
  challengeVisible: boolean;
  /**
   * Выполняет запрос с токеном капчи. На 400 captcha_required инвалидирует конфиг,
   * перезагружает виджет и один раз повторяет запрос; иначе ошибка уходит в inline-обработчик.
   */
  run: <T>(request: (captchaToken: string) => Promise<T>) => Promise<T>;
};

/**
 * Невидимая Yandex SmartCaptcha: чекбокса нет, задание показывается только
 * подозрительным пользователям. Токен запрашивается на сабмит через `execute()`.
 */
export function useSmartCaptcha(): SmartCaptchaController {
  const { status, config, error, refresh, reload } = useAuthConfig();
  const [challengeVisible, setChallengeVisible] = useState(false);

  const configRef = useRef<AuthConfig | null>(config);
  const widgetIdRef = useRef<number | null>(null);
  const widgetKeyRef = useRef<string | null>(null);
  const setupRef = useRef<Promise<number | null> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pendingRef = useRef<{ resolve: (token: string) => void; reject: (e: Error) => void; timer: number } | null>(
    null
  );
  const pendingPromiseRef = useRef<Promise<string> | null>(null);
  const unsubsRef = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (config) configRef.current = config;
  }, [config]);

  const teardown = useCallback(() => {
    for (const unsub of unsubsRef.current) {
      try {
        unsub();
      } catch {
        /* noop */
      }
    }
    unsubsRef.current = [];
    const p = pendingRef.current;
    if (p) {
      clearTimeout(p.timer);
      pendingRef.current = null;
      pendingPromiseRef.current = null;
      p.reject(new Error('SmartCaptcha: страница закрыта'));
    }
    if (widgetIdRef.current != null && window.smartCaptcha) {
      try {
        window.smartCaptcha.destroy(widgetIdRef.current);
      } catch {
        /* noop */
      }
    }
    widgetIdRef.current = null;
    widgetKeyRef.current = null;
    setupRef.current = null;
    containerRef.current?.remove();
    containerRef.current = null;
    setChallengeVisible(false);
  }, []);

  const setupWidget = useCallback(
    (cfg: AuthConfig, opts?: { force?: boolean }): Promise<number | null> => {
      if (!isCaptchaEnabled(cfg)) {
        teardown();
        return Promise.resolve(null);
      }
      const sitekey = cfg.captcha_sitekey;
      if (!opts?.force && widgetKeyRef.current === sitekey && setupRef.current) {
        return setupRef.current;
      }
      teardown();

      const container = document.createElement('div');
      container.setAttribute('data-domcrm-smartcaptcha', '');
      container.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;overflow:hidden;';
      document.body.appendChild(container);
      containerRef.current = container;
      widgetKeyRef.current = sitekey;

      const settle = (token: string) => {
        const p = pendingRef.current;
        if (!p) return;
        clearTimeout(p.timer);
        pendingRef.current = null;
        pendingPromiseRef.current = null;
        p.resolve(token);
      };
      const fail = (message: string) => {
        const p = pendingRef.current;
        if (!p) return;
        clearTimeout(p.timer);
        pendingRef.current = null;
        pendingPromiseRef.current = null;
        p.reject(new Error(message));
      };

      const ready: Promise<number | null> = loadSmartCaptchaScript()
        .then(() => {
          if (containerRef.current !== container) throw new Error('SmartCaptcha: виджет перезагружен');
          const api = window.smartCaptcha;
          if (!api) throw new Error('SmartCaptcha недоступна');
          const widgetId = api.render(container, {
            sitekey,
            hl: 'ru',
            invisible: true,
            hideShield: true,
            ...(isMobileWebView() ? { webview: true } : {}),
            callback: (token: string) => {
              setChallengeVisible(false);
              if (token) settle(token);
            }
          });
          widgetIdRef.current = widgetId;
          unsubsRef.current = [
            api.subscribe(widgetId, 'challenge-visible', () => setChallengeVisible(true)),
            api.subscribe(widgetId, 'challenge-hidden', () => setChallengeVisible(false)),
            api.subscribe(widgetId, 'token-expired', () => fail('Проверка устарела. Попробуйте ещё раз.')),
            api.subscribe(widgetId, 'network-error', () => fail('Не удалось пройти проверку. Попробуйте ещё раз.')),
            api.subscribe(widgetId, 'javascript-error', () => fail('Не удалось пройти проверку. Попробуйте ещё раз.'))
          ];
          return widgetId;
        })
        .catch((e) => {
          fail(e instanceof Error ? e.message : 'Не удалось загрузить проверку');
          throw e;
        });
      setupRef.current = ready;
      ready.catch(() => {
        /* ошибка уже обработана в execute() */
      });
      return ready;
    },
    [teardown]
  );

  useEffect(() => {
    if (status !== 'ready' || !config) return;
    void setupWidget(config).catch(() => {
      /* ошибка виджета показывается при попытке отправки */
    });
  }, [status, config, setupWidget]);

  useEffect(() => () => teardown(), [teardown]);

  const executeForConfig = useCallback(
    async (cfg: AuthConfig | null): Promise<string> => {
      if (!isCaptchaEnabled(cfg)) return '';
      const current = cfg as AuthConfig;
      const sitekey = current.captcha_sitekey;
      if (pendingPromiseRef.current) return pendingPromiseRef.current;
      let ready = setupRef.current;
      if (!ready || widgetKeyRef.current !== sitekey) {
        ready = setupWidget(current);
      }
      const widgetId = await ready;
      const api = window.smartCaptcha;
      if (widgetId == null || !api) throw new Error('Проверка ещё не готова. Попробуйте ещё раз.');
      api.reset(widgetId);
      const promise = new Promise<string>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          pendingRef.current = null;
          pendingPromiseRef.current = null;
          reject(new Error('Проверка не завершена. Попробуйте ещё раз.'));
        }, CAPTCHA_TIMEOUT_MS);
        pendingRef.current = { resolve, reject, timer };
      });
      pendingPromiseRef.current = promise;
      api.execute(widgetId);
      return promise;
    },
    [setupWidget]
  );

  const execute = useCallback(() => executeForConfig(configRef.current ?? config), [config, executeForConfig]);

  const reset = useCallback(() => {
    const p = pendingRef.current;
    if (p) {
      clearTimeout(p.timer);
      pendingRef.current = null;
      pendingPromiseRef.current = null;
    }
    if (window.smartCaptcha && widgetIdRef.current != null) {
      try {
        window.smartCaptcha.reset(widgetIdRef.current);
      } catch {
        /* noop */
      }
    }
    setChallengeVisible(false);
  }, []);

  const run = useCallback(
    async <T,>(request: (captchaToken: string) => Promise<T>): Promise<T> => {
      let cfg = configRef.current;
      if (!cfg) {
        try {
          cfg = await reload(false);
          configRef.current = cfg;
        } catch {
          cfg = null;
        }
      }
      const token = await executeForConfig(cfg);
      try {
        return await request(token);
      } catch (err) {
        if (!isCaptchaRequiredError(err)) throw err;
        // Сервер ждёт капчу, а локальный конфиг мог быть устаревшим/не загрузиться.
        // Инвалидируем, перезагружаем конфиг и виджет, затем повторяем запрос один раз.
        try {
          const fresh = await reload(true);
          configRef.current = fresh;
          await setupWidget(fresh, { force: true });
        } catch {
          throw err;
        }
        const retryToken = await executeForConfig(configRef.current);
        return await request(retryToken);
      }
    },
    [executeForConfig, reload, setupWidget]
  );

  return {
    enabled: isCaptchaEnabled(config),
    serverEnabled: isCaptchaServerEnabled(config),
    bypass: Boolean(config?.captcha_bypass),
    status,
    error,
    loading: status === 'loading',
    canSubmit: status !== 'loading',
    refresh,
    execute,
    reset,
    challengeVisible,
    run
  };
}
